import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info, CheckCircle2, Ban, Wrench } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Banner-type → visual config. All banners render in BBA brand colours
 * (navy #1A1A2E background, white text, orange #FF6B00 bottom border).
 * Only the leading icon changes per type.
 */
const BANNER_STYLES = {
  warning:     { Icon: AlertTriangle, emoji: '⚠️' },
  danger:      { Icon: Ban,           emoji: '🚫' },
  info:        { Icon: Info,          emoji: 'ℹ️' },
  success:     { Icon: CheckCircle2,  emoji: '✅' },
  maintenance: { Icon: Wrench,        emoji: '🔧' },
};

/**
 * Pure presentational — used by SiteBanner (live) and by the admin Preview box.
 * Accepts either a full banner object or discrete props.
 */
export function BannerBody({ message_text, banner_type = 'warning' }) {
  const style = BANNER_STYLES[banner_type] || BANNER_STYLES.warning;
  const { Icon } = style;
  return (
    <div
      className="py-1.5 px-4 text-center font-bold shadow-lg"
      style={{
        background: '#1A1A2E',
        color: '#FFFFFF',
        borderBottom: '2px solid #FF6B00',
        fontSize: '12px',
      }}
      data-testid="site-banner-body"
    >
      <div className="flex items-center justify-center gap-2">
        <Icon className="w-4 h-4" style={{ color: '#FF6B00' }} />
        <span>
          {style.emoji} {message_text} {style.emoji}
        </span>
        <Icon className="w-4 h-4" style={{ color: '#FF6B00' }} />
      </div>
    </div>
  );
}

/**
 * Live banner — fetches from GET /api/admin/site-banner on mount, renders only
 * when is_active=true AND show_on matches this page's `scope`.
 *
 * Usage:
 *   <SiteBanner scope="customer" />   // LandingPage, AuthPage, etc.
 *   <SiteBanner scope="admin" />      // AdminLayout
 */
export default function SiteBanner({ scope = 'customer' }) {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/site-banner`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setBanner(data);
      } catch {
        // Silent — banner is non-critical. Page must still render.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!banner || !banner.is_active) return null;

  const { show_on, message_text, banner_type } = banner;
  const scopeMatches =
    show_on === 'all' ||
    (show_on === 'customer_only' && scope === 'customer') ||
    (show_on === 'admin_only' && scope === 'admin');

  if (!scopeMatches) return null;

  return (
    <div className="sticky top-0 z-50" data-testid={`site-banner-${scope}`}>
      <BannerBody message_text={message_text} banner_type={banner_type} />
    </div>
  );
}
